/**
 * ContactPill — Inline pill component for client contact attributions in AI responses.
 *
 * Rendered by ChatMessage when ReactMarkdown encounters a `contact:<email>` link.
 * The amber colouring distinguishes client contacts from internal team member pills
 * (which use teal via PersonPill).
 *
 * Returns null for malformed or empty email addresses so the fallthrough
 * renders plain text rather than a broken pill.
 */

/** Basic email format validation — matches the same regex used in ChatMessage's `a` handler. */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface ContactPillProps {
	email: string;
	displayName: string;
}

export default function ContactPill({ email, displayName }: ContactPillProps) {
	// Guard: suppress the pill entirely for malformed or empty emails
	if (!EMAIL_REGEX.test(email)) {
		return null;
	}

	return (
		<span
			className="contact-pill"
			data-email={email}
			aria-label={`Client contact: ${displayName}`}
		>
			{/* Briefcase icon — 12px inline SVG, aria-hidden as the aria-label covers semantics */}
			<svg
				viewBox="0 0 12 12"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
				width="12"
				height="12"
				aria-hidden="true"
				style={{ flexShrink: 0 }}
			>
				{/* Briefcase handle */}
				<path
					d="M4 3V2.5C4 2 4.5 1.5 5 1.5H7C7.5 1.5 8 2 8 2.5V3"
					stroke="currentColor"
					strokeWidth="1.1"
					strokeLinecap="round"
				/>
				{/* Briefcase body */}
				<rect
					x="1.5"
					y="3"
					width="9"
					height="7"
					rx="1.2"
					stroke="currentColor"
					strokeWidth="1.1"
				/>
				{/* Briefcase centre divider */}
				<path
					d="M1.5 6.5H10.5"
					stroke="currentColor"
					strokeWidth="1.1"
					strokeLinecap="round"
				/>
			</svg>
			{displayName}
		</span>
	);
}
